import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChartLine,
  faCheck,
  faComments,
  faCopy,
  faDatabase,
  faExternalLink,
  faGlobe,
} from "@fortawesome/free-solid-svg-icons";
import api, { getData } from "../services/api";
import { useToast } from "../context/ToastContext";
import { formatDate } from "../utils/format";
import { getPublicAppUrl } from "../utils/publicUrl";
import {
  Button,
  ErrorState,
  PageHeader,
  Skeleton,
  StatusBadge,
} from "../components/UI";

export default function WebsiteDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const [state, setState] = useState({
    loading: true,
    website: null,
    error: "",
  });
  const load = useCallback(
    () =>
      api
        .get(`/websites/${id}`)
        .then((r) =>
          setState({ loading: false, website: getData(r).website, error: "" }),
        )
        .catch((e) =>
          setState({ loading: false, website: null, error: e.message }),
        ),
    [id],
  );
  useEffect(load, [load]);
  if (state.loading) return <Skeleton rows={7} />;
  if (state.error) return <ErrorState message={state.error} onRetry={load} />;
  const site = state.website;
  const base = getPublicAppUrl();
  const snippet = `<script src="${base}/chatbot.js" data-website-id="${site._id}"></script>`;
  const copy = async () => {
    await navigator.clipboard.writeText(snippet);
    toast.show("Installation snippet copied.");
  };
  return (
    <>
      <PageHeader
        eyebrow="Website overview"
        title={site.name}
        description={
          site.description || `Assistant operations for ${site.domain}.`
        }
        actions={
          <Button
            variant="secondary"
            icon={faExternalLink}
            onClick={() =>
              window.open(
                `https://${site.domain}`,
                "_blank",
                "noopener,noreferrer",
              )
            }
          >
            Visit domain
          </Button>
        }
      />
      <section className="detail-hero panel">
        <div className="detail-ident">
          <span className="large-site-avatar">{site.name[0]}</span>
          <div>
            <h2>{site.domain}</h2>
            <div className="badge-row">
              <StatusBadge active={site.status === "active"} />
              <StatusBadge active={site.chatbotEnabled}>
                {site.chatbotEnabled ? "Chatbot enabled" : "Chatbot disabled"}
              </StatusBadge>
            </div>
          </div>
        </div>
        <dl>
          <div>
            <dt>Knowledge records</dt>
            <dd>{site.knowledgeRecords || 0}</dd>
          </div>
          <div>
            <dt>Current version</dt>
            <dd>v{site.knowledgeVersion}</dd>
          </div>
          <div>
            <dt>Last updated</dt>
            <dd>{formatDate(site.updatedAt)}</dd>
          </div>
        </dl>
      </section>
      <section className="detail-grid">
        <article className="panel install-panel">
          <span className="eyebrow">Install widget</span>
          <h2>One script, domain-verified.</h2>
          <p>Add this before the closing body tag on an allowed domain.</p>
          <div className="code-block">
            <code>{snippet}</code>
            <button onClick={copy} aria-label="Copy installation snippet">
              <FontAwesomeIcon icon={faCopy} />
            </button>
          </div>
          <div className="verification-note">
            <FontAwesomeIcon icon={faCheck} />
            <span>
              Configured domains:{" "}
              {(site.allowedDomains || [site.domain]).join(", ")}
            </span>
          </div>
        </article>
        <aside className="section-links">
          <button onClick={() => navigate(`/websites/${id}/knowledge`)}>
            <span>
              <FontAwesomeIcon icon={faDatabase} />
            </span>
            <div>
              <strong>Knowledge manager</strong>
              <small>Records, versions, imports</small>
            </div>
          </button>
          <button onClick={() => navigate(`/websites/${id}/chatbot`)}>
            <span>
              <FontAwesomeIcon icon={faComments} />
            </span>
            <div>
              <strong>Configure chatbot</strong>
              <small>Appearance, model, behavior</small>
            </div>
          </button>
          <button onClick={() => navigate(`/websites/${id}/analytics`)}>
            <span>
              <FontAwesomeIcon icon={faChartLine} />
            </span>
            <div>
              <strong>View analytics</strong>
              <small>Sessions and top questions</small>
            </div>
          </button>
        </aside>
      </section>
    </>
  );
}
